import { useState, useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const authCheckedRef = useRef(false);

  // Check if already authenticated on mount
  useEffect(() => {
    if (authCheckedRef.current) return;
    authCheckedRef.current = true;
    
    try {
      const storedAuth = sessionStorage.getItem('adminAuth');
      if (storedAuth) {
        setIsAuthenticated(true);
      }
    } catch (err) {
      // SessionStorage not available (private browsing, etc.)
      console.warn('SessionStorage not available:', err.message);
    }
  }, []);

  const getAuthHeader = useCallback(() => {
    try {
      const storedAuth = sessionStorage.getItem('adminAuth');
      if (storedAuth) {
        const { username, password } = JSON.parse(storedAuth);
        return 'Basic ' + btoa(`${username}:${password}`);
      }
    } catch (err) {
      console.warn('Failed to get auth header:', err.message);
    }
    return '';
  }, []);

  const login = useCallback(async (username, password) => {
    setLoginLoading(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        }
      });
      
      if (response.ok) {
        try {
          sessionStorage.setItem('adminAuth', JSON.stringify({ username, password }));
        } catch (storageErr) {
          console.warn('Failed to store auth:', storageErr.message);
        }
        setIsAuthenticated(true);
        return true;
      } else {
        setLoginError('Invalid username or password');
        return false;
      }
    } catch (err) {
      setLoginError(err.message || 'Connection error. Please try again.');
      return false;
    } finally {
      setLoginLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem('adminAuth');
    } catch (err) {
      console.warn('Failed to clear auth:', err.message);
    }
    setIsAuthenticated(false);
  }, []);

  return {
    isAuthenticated,
    loginLoading,
    loginError,
    login,
    logout,
    getAuthHeader
  };
};

export default useAdminAuth;
